import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlarmClock,
  BellRing,
  CalendarClock,
  CircleDollarSign,
  ClockAlert,
  LogIn,
  LogOut,
  ReceiptText,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/useLocale";
import {
  useGetChatSettingsQuery,
  useSetChannelNoticeMutation,
} from "@/services/(my-space)/api.chat";
import type {
  ChatNoticeKind,
  IChatMessage,
  IChatNoticeMetadata,
} from "@/types/(my-space)/chat.interface";
import { CHAT_NOTICE_KINDS } from "@/types/(my-space)/chat.interface";
import { cn } from "@/utils/cn";
import {
  featureUpdateOf,
  formatDecimalHours,
  formatHoursMinutes,
  noticeAction,
  noticeBody,
  noticeTitle,
  weeklyHoursOf,
  type WeeklyHours,
} from "../_lib/channel-notices";
import { parseShortcutDate } from "../_lib/shortcut-format";
import { MessageText } from "./MessageText";

const KIND_ICONS: Partial<Record<ChatNoticeKind, LucideIcon>> = {
  checkin_reminder: AlarmClock,
  late_checkin_reminder: ClockAlert,
  checkout_reminder: AlarmClock,
  late_checkout_reminder: ClockAlert,
  auto_clock_out: CalendarClock,
  admin_clocked_in: LogIn,
  admin_clocked_out: LogOut,
  payslip_ready: ReceiptText,
  salary_paid: CircleDollarSign,
};

const isKnownKind = (kind: string): kind is ChatNoticeKind =>
  (CHAT_NOTICE_KINDS as readonly string[]).includes(kind);

/**
 * A channel's notice: a full-width card (not a bubble — nobody sent it), its
 * time, and — until answered once per kind — "Is this message useful to you?".
 */
export const NoticeMessage = ({ message: m, compact }: { message: IChatMessage; compact?: boolean }) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const notice = m.metadata?.notice;
  if (!notice) return null;
  const action = noticeAction(t, notice);
  const Icon = KIND_ICONS[notice.kind as ChatNoticeKind] ?? BellRing;
  const body = noticeBody(t, notice);

  if (notice.kind === "feature_update") {
    return <FeatureUpdateCard message={m} notice={notice} compact={compact} />;
  }

  return (
    <div className={cn("flex py-1", compact ? "px-2.5" : "px-3 md:px-5")}>
      <article
        className={cn(
          "flex w-full flex-col gap-3 rounded-2xl border border-border bg-surface p-4 shadow-xs",
          compact ? "max-w-full" : "max-w-md",
        )}
      >
        {notice.kind === "weekly_hours" ? (
          <WeeklyHoursCard report={weeklyHoursOf(notice)} />
        ) : (
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-info-muted text-info">
              <Icon className="size-5" />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <h3 className="text-sm font-semibold text-foreground">{noticeTitle(t, notice)}</h3>
              {body ? <p className="text-[13px] leading-snug text-muted-foreground">{body}</p> : null}
            </div>
          </div>
        )}

        {action ? (
          <Button variant="outline" size="sm" className="w-full rounded-full" onClick={() => navigate(action.path)}>
            {action.label}
          </Button>
        ) : null}

        <span className="text-[11px] text-muted-foreground">{formatDate(new Date(m.createdAt), "p")}</span>

        {isKnownKind(notice.kind) ? <UsefulPrompt notice={notice} kind={notice.kind} /> : null}
      </article>
    </div>
  );
};

/**
 * A MaiHRMS product update: its picture or video edge to edge, the headline,
 * then the text (Markdown, like a message). No question under it — it's news,
 * not a reminder; it can still be turned off in the channel's settings.
 */
const FeatureUpdateCard = ({
  message: m,
  notice,
  compact,
}: {
  message: IChatMessage;
  notice: IChatNoticeMetadata;
  compact?: boolean;
}) => {
  const { t } = useTranslation("chat");
  const { formatDate } = useLocale();
  const update = featureUpdateOf(notice);
  return (
    <div className={cn("flex py-1", compact ? "px-2.5" : "px-3 md:px-5")}>
      <article
        className={cn(
          "flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xs",
          compact ? "max-w-full" : "max-w-md",
        )}
      >
        {update.mediaUrl ? (
          update.mediaKind === "video" ? (
            <video
              src={update.mediaUrl}
              controls
              playsInline
              preload="metadata"
              className="aspect-video w-full bg-black object-contain"
            />
          ) : (
            <img
              src={update.mediaUrl}
              alt=""
              loading="lazy"
              className="max-h-80 w-full bg-muted object-cover"
            />
          )
        ) : null}
        <div className="flex flex-col gap-1.5 p-4">
          {update.title ? (
            <h3 className="text-[15px] leading-snug font-semibold text-foreground">{update.title}</h3>
          ) : null}
          {update.body ? (
            <MessageText text={update.body} isMine={false} className="text-sm text-foreground" />
          ) : null}
          <span className="mt-1 flex gap-1 self-end text-[11px] text-muted-foreground">
            {/* MaiHRMS corrected it after sending. */}
            {m.editedAt ? <span>{t("labels.edited")}</span> : null}
            <span>{formatDate(new Date(m.createdAt), "p")}</span>
          </span>
        </div>
      </article>
    </div>
  );
};

const WeeklyHoursCard = ({ report }: { report: WeeklyHours }) => {
  const { t } = useTranslation("chat");
  const { formatDate, formatDateRange } = useLocale();
  const start = parseShortcutDate(report.weekStart);
  const end = parseShortcutDate(report.weekEnd);
  // Bars share one scale; a light week isn't drawn as full days.
  const scale = Math.max(8 * 60, ...report.days.map((d) => d.workedMinutes));
  const overtimeShare = report.totalMinutes ? report.overtimeMinutes / report.totalMinutes : 0;

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{t("notice.weekly_hours.title")}</h3>
        {start && end ? (
          <p className="text-xs text-muted-foreground">
            {formatDateRange(start, end, { day: "numeric", month: "short", year: "numeric" })}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-7 divide-x divide-border/70" role="list" aria-label={t("notice.weekly_hours.days")}>
        {report.days.map((d) => {
          const date = parseShortcutDate(d.date);
          const pct = Math.min(100, (d.workedMinutes / scale) * 100);
          return (
            <div key={d.date} role="listitem" className="flex flex-col items-center gap-1.5 px-0.5">
              <span className="text-[11px] text-muted-foreground">{date ? formatDate(date, "EEE") : ""}</span>
              <div className="flex h-20 w-full items-end justify-center">
                {d.workedMinutes > 0 ? (
                  <div
                    className={cn(
                      "w-3/4 max-w-7 rounded-t-md",
                      d.overtimeMinutes > 0 ? "bg-warning/80" : "bg-info/70",
                    )}
                    style={{ height: `${Math.max(pct, 6)}%` }}
                    title={formatHoursMinutes(d.workedMinutes)}
                  />
                ) : (
                  <div className="w-3/4 max-w-7 border-t border-dashed border-border" />
                )}
              </div>
              <span className="text-[11px] tabular-nums text-foreground">
                {d.workedMinutes > 0 ? formatHoursMinutes(d.workedMinutes) : "--"}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="text-sm text-foreground">{t("notice.weekly_hours.total")}</span>
        <span className="text-base font-bold tabular-nums text-foreground">
          {formatHoursMinutes(report.totalMinutes)}
        </span>
      </div>
      {report.totalMinutes > 0 ? (
        <div className="flex h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className="bg-info/70" style={{ width: `${(1 - overtimeShare) * 100}%` }} />
          <div className="bg-warning/80" style={{ width: `${overtimeShare * 100}%` }} />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="flex-1 rounded-md bg-muted px-2.5 py-1.5 text-center tabular-nums text-foreground">
          {t("notice.weekly_hours.regular", { hours: formatDecimalHours(report.regularMinutes) })}
        </span>
        {report.overtimeMinutes > 0 ? (
          <span className="flex-1 rounded-md bg-warning-muted px-2.5 py-1.5 text-center tabular-nums text-warning">
            {t("notice.weekly_hours.overtime", { hours: formatDecimalHours(report.overtimeMinutes) })}
          </span>
        ) : null}
      </div>
    </div>
  );
};

/** Asked once per kind; the answer also shows in the channel's settings. */
const UsefulPrompt = ({ notice, kind }: { notice: IChatNoticeMetadata; kind: ChatNoticeKind }) => {
  const { t } = useTranslation("chat");
  const settings = useGetChatSettingsQuery();
  const [setNotice, { isLoading }] = useSetChannelNoticeMutation();
  // Unknown until loaded: don't flash the question.
  if (!settings.data || settings.data.channelNotices?.[kind] !== undefined) return null;

  const answer = async (keep: boolean) => {
    try {
      await setNotice({ kind, keep }).unwrap();
      if (!keep) toast.success(t("notice.stopped", { title: noticeTitle(t, notice) }));
    } catch {
      toast.error(t("notice.answer_failed"));
    }
  };

  return (
    <div className="rounded-xl bg-muted/60 p-3">
      <p className="text-sm text-foreground">{t("notice.useful_question")}</p>
      <div className="mt-1.5 flex flex-wrap gap-4 text-sm font-medium">
        <button type="button" disabled={isLoading} onClick={() => void answer(true)} className="text-info hover:underline disabled:opacity-60">
          {t("notice.useful_yes")}
        </button>
        <button type="button" disabled={isLoading} onClick={() => void answer(false)} className="text-danger hover:underline disabled:opacity-60">
          {t("notice.useful_no")}
        </button>
      </div>
    </div>
  );
};
